import { FC, useCallback, useMemo } from 'react';
import { FormProvider, Path, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { buttonStyle } from 'components/buttons/buttonStyle';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import Section from 'components/section-menu/Section';
import SectionLayout from 'components/section-menu/SectionLayout';
import { Button } from 'components/ui/button';
import { ERPDto, ERPForm } from 'types/ndf';
import { downloadFile } from 'utils/downloadFile';
import { t } from 'utils/getTranslations';
import {
    isDateEarlierThan,
    isDateLaterThan,
    isValidDateRange,
    requireCurrentOrSpecifiedFields,
} from 'utils/validators';
import { NDF_ERP_ENDPOINT, NDF_FORM_MAX_DATERANGE_DAYS_DIFFERENCE } from './ndfConstants';

const defaultValues: ERPForm = {
    invoiceNumber: '',
    invoiceStartDate: null,
    invoiceEndDate: null,
    serviceStartDate: null,
    serviceEndDate: null,
    shouldIncludeAlreadyExported: false,
};

/**
 * Helper type to extract the type of a property at a given Path.
 */
type GetPathValue<T, P extends Path<T>> = P extends keyof T
    ? T[P]
    : P extends `${infer K}.${infer Rest}`
      ? K extends keyof T
          ? T[K] extends object
              ? GetPathValue<T[K], Rest extends Path<T[K]> ? Rest : never>
              : never
          : never
      : never;

/**
 * Type utility to get paths of properties in T where the value is assignable to string | null.
 */
type NullableStringPath<T> = {
    [K in Path<T>]: NonNullable<GetPathValue<T, K>> extends string ? K : never;
}[Path<T>];

const NDFExportERPForm: FC = () => {
    const methods = useForm<ERPForm>({ defaultValues, mode: 'onChange' });
    const { control, handleSubmit, trigger } = methods;

    const onSubmit = useCallback((formData: ERPForm) => {
        const data = mapToERPDto(formData);
        const queryParams = new URLSearchParams(Object.entries(data).filter(([, value]) => value !== null)).toString();
        downloadFile(`${NDF_ERP_ENDPOINT}?${queryParams}`);
    }, []);

    const actions = useMemo(() => {
        return [
            <Button
                key="ndf-export"
                {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                onClick={() => {
                    void handleSubmit(onSubmit)();
                }}
            >
                <FormattedMessage id="ndf.export" />
            </Button>,
        ];
    }, [handleSubmit, onSubmit]);

    const requiredFieldsValidator = useCallback(
        (value: unknown, formValues: ERPForm, requiredFields: Path<ERPForm>[]) => {
            if (formValues.invoiceNumber && formValues.invoiceNumber.trim()) {
                return true;
            }
            return requireCurrentOrSpecifiedFields(
                value,
                formValues,
                requiredFields,
                t('validation.requires-at-least-one-of-fields', {
                    fields: [t('ndf.erp.invoice-date'), t('ndf.erp.services-date')].join(', '),
                })
            );
        },
        []
    );

    const dateRangeValidator = useCallback(
        (from: NullableStringPath<ERPForm>, to: NullableStringPath<ERPForm>, formValues: ERPForm) =>
            isValidDateRange(formValues[from] ?? '', formValues[to] ?? '', NDF_FORM_MAX_DATERANGE_DAYS_DIFFERENCE),
        []
    );

    return (
        <FormProvider {...methods}>
            <FormWrapper>
                <ActionHeader title={t('menu.ndf-export')} actions={actions} />
                <SectionLayout>
                    <Section title="menu.sunsystems-erp">
                        <div className="max-w-[576px]">
                            <div className="flex flex-col gap-4">
                                <TextInput
                                    control={control}
                                    name="invoiceNumber"
                                    label="ndf.erp.invoice-number"
                                    maxLength={100}
                                    onChange={() => {
                                        trigger([
                                            'invoiceStartDate',
                                            'invoiceEndDate',
                                            'serviceStartDate',
                                            'serviceEndDate',
                                        ]);
                                    }}
                                />
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <DateInput
                                            control={control}
                                            name="invoiceStartDate"
                                            label="ndf.erp.invoice-date-from"
                                            onChange={() => {
                                                trigger(['invoiceEndDate', 'serviceStartDate', 'serviceEndDate']);
                                            }}
                                            fullWidth
                                            validate={{
                                                require: (value, formFields) =>
                                                    requiredFieldsValidator(value, formFields, [
                                                        'serviceStartDate',
                                                        'serviceEndDate',
                                                    ]),
                                                isEarlierThan: (_, formValues) => {
                                                    return isDateEarlierThan(
                                                        formValues.invoiceStartDate ?? '',
                                                        formValues.invoiceEndDate ?? '',
                                                        t('ndf.erp.invoice-date-to')
                                                    );
                                                },
                                                isValidRange: (_, formValues) =>
                                                    dateRangeValidator(
                                                        'invoiceStartDate',
                                                        'invoiceEndDate',
                                                        formValues
                                                    ),
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <DateInput
                                            control={control}
                                            name="invoiceEndDate"
                                            label="ndf.erp.invoice-date-to"
                                            onChange={() => {
                                                trigger(['invoiceStartDate', 'serviceStartDate', 'serviceEndDate']);
                                            }}
                                            fullWidth
                                            validate={{
                                                require: (value, formFields) =>
                                                    requiredFieldsValidator(value, formFields, [
                                                        'serviceStartDate',
                                                        'serviceEndDate',
                                                    ]),
                                                isLaterThan: (_, formValues) => {
                                                    return isDateLaterThan(
                                                        formValues.invoiceEndDate ?? '',
                                                        formValues.invoiceStartDate ?? '',
                                                        t('ndf.erp.invoice-date-from')
                                                    );
                                                },
                                                isValidRange: (_, formValues) =>
                                                    dateRangeValidator(
                                                        'invoiceStartDate',
                                                        'invoiceEndDate',
                                                        formValues
                                                    ),
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <DateInput
                                            control={control}
                                            name="serviceStartDate"
                                            label="ndf.erp.services-date-from"
                                            onChange={() => {
                                                trigger(['serviceEndDate', 'invoiceStartDate', 'invoiceEndDate']);
                                            }}
                                            fullWidth
                                            validate={{
                                                require: (value, formFields) =>
                                                    requiredFieldsValidator(value, formFields, [
                                                        'invoiceStartDate',
                                                        'invoiceEndDate',
                                                    ]),
                                                isEarlierThan: (_, formValues) => {
                                                    return isDateEarlierThan(
                                                        formValues.serviceStartDate ?? '',
                                                        formValues.serviceEndDate ?? '',
                                                        t('ndf.erp.services-date-to')
                                                    );
                                                },
                                                isValidRange: (_, formValues) =>
                                                    dateRangeValidator(
                                                        'serviceStartDate',
                                                        'serviceEndDate',
                                                        formValues
                                                    ),
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <DateInput
                                            control={control}
                                            name="serviceEndDate"
                                            label="ndf.erp.services-date-to"
                                            onChange={() => {
                                                trigger(['serviceStartDate', 'invoiceStartDate', 'invoiceEndDate']);
                                            }}
                                            fullWidth
                                            validate={{
                                                require: (value, formFields) =>
                                                    requiredFieldsValidator(value, formFields, [
                                                        'invoiceStartDate',
                                                        'invoiceEndDate',
                                                    ]),
                                                isLaterThan: (_, formValues) => {
                                                    return isDateLaterThan(
                                                        formValues.serviceEndDate ?? '',
                                                        formValues.serviceStartDate ?? '',
                                                        t('ndf.erp.services-date-from')
                                                    );
                                                },
                                                isValidRange: (_, formValues) =>
                                                    dateRangeValidator(
                                                        'serviceStartDate',
                                                        'serviceEndDate',
                                                        formValues
                                                    ),
                                            }}
                                        />
                                    </div>
                                    <div>
                                        <CheckboxInput
                                            control={control}
                                            name="shouldIncludeAlreadyExported"
                                            label="ndf.erp.should-include-exported"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Section>
                </SectionLayout>
            </FormWrapper>
        </FormProvider>
    );
};

const mapToERPDto = (form: ERPForm): ERPDto => ({
    invoiceNumber: form.invoiceNumber || null,
    invoiceStartDate: form.invoiceStartDate || null,
    invoiceEndDate: form.invoiceEndDate || null,
    serviceStartDate: form.serviceStartDate || null,
    serviceEndDate: form.serviceEndDate || null,
    shouldIncludeAlreadyExported: form.shouldIncludeAlreadyExported,
});

export default NDFExportERPForm;
